import { pickOgPhoto, tidyText } from "@/lib/og";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { parseUnifiedSlug } from "@/lib/catalog.functions";
import { getPresentationPrices } from "@/lib/presentation-prices.functions";
import { getApprovedReviews } from "@/lib/verified-reviews.functions";
import { cleanTourTitle, metaDescription } from "@/lib/presentation-text";
import { stripHtml } from "@/lib/slug";
import { buildTourHead, toAbsoluteUrl } from "@/lib/tour-seo";
import { isRetiredTour, RETIRED_REDIRECT_CITY } from "@/lib/retired-tours";
import { pinnedSlugRedirect } from "@/lib/canonical-tour-slugs";
import {
  TourDetailShell,
  TourNotFoundView,
  tourQuery,
} from "@/components/tour/TourDetailShell";

export const Route = createFileRoute("/tour/$slug")({
  beforeLoad: ({ params, search }) => {
    // Pinned products: older/translated slugs → canonical in one 301 hop,
    // keeping tracking params (utm_*, etc.).
    const pinned = pinnedSlugRedirect(params.slug);
    if (pinned) {
      throw redirect({
        to: "/tour/$slug",
        params: { slug: pinned },
        search: search as never,
        statusCode: 301,
      });
    }
    // Retired products: URL stays alive and permanently redirects to the
    // Cancún tours landing so accumulated authority is transferred.
    if (isRetiredTour(params.slug)) {
      throw redirect({
        to: "/tours/$city",
        params: { city: RETIRED_REDIRECT_CITY },
        statusCode: 301,
      });
    }
  },
  loader: async ({ params, context }) => {
    const { source, key } = parseUnifiedSlug(params.slug);
    const result = await context.queryClient.ensureQueryData(
      tourQuery(source, key, context.lang),
    );
    if (!result.tour) throw notFound();
    const t = result.tour;
    const short = stripHtml(t.summary || "").trim();
    const description = metaDescription(/[.!?]$/.test(short) ? short : t.description || short);
    const image = toAbsoluteUrl(pickOgPhoto(t.images));

    const prices = source === "bokun" ? await getPresentationPrices({data:{ids:[key]}}) : {};
    const finalPrice = prices[key] ?? null;
    if (source === "bokun") context.queryClient.setQueryData(["tour-card-price",key],finalPrice);
    const rating = source === "bokun" ? await getApprovedReviews({data:{productId:key}}) : { avg:0,count:0,reviews:[] };
    if (source === "bokun") context.queryClient.setQueryData(["verified-reviews",key],rating);
    return {
      source,
      key,
      title: cleanTourTitle(t.title),
      description,
      image,
      cardImage: t.images?.[0],
      place: t.location ?? null,
      priceFrom: finalPrice?.amount ?? null,
      prices,
      reviews: rating.reviews,
      currency: finalPrice?.currency ?? "MXN",
      ratingAvg: rating.avg,
      ratingCount: rating.count,
    };

  },
  head: ({ loaderData, params }) =>
    buildTourHead({ loaderData, slug: params.slug, lang: "es" }),
  component: TourDetailPage,
  notFoundComponent: TourNotFoundView,
  errorComponent: ({ error }) => (
    <div className="p-12 text-center text-sm text-destructive">{error instanceof Error ? error.message : String(error)}</div>
  ),
});

function TourDetailPage() {
  const { source, key, prices } = Route.useLoaderData();
  const { slug } = Route.useParams();
  return <TourDetailShell source={source} idKey={key} slug={slug} presentationPrices={prices} />;
}
